import { useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, CopyButton, Group, Paper, PasswordInput, SegmentedControl, Select, Stack, Text,
  TextInput, Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { IconCheck, IconCopy, IconKey, IconRefresh, IconShieldLock, IconSparkles, IconUserPlus } from '@tabler/icons-react';
import api from '../services/api';
import { BlockLoader } from './BlockLoader';
import {
  generateStrongPassword, LOGIN_MIN, TENANT_ADMIN_PASSWORD_MIN, FULL_NAME_MIN, FULL_NAME_MAX, cleanFullName, fullNameError,
} from '../lib/password';

const errMsg = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

/** Nusxalash tugmasi (parol/login) — kichik ikonka. */
function CopyIcon({ value }: { value: string }) {
  const { t } = useTranslation();
  return (
    <CopyButton value={value} timeout={1500}>
      {({ copied, copy }) => (
        <Tooltip label={copied ? t('tenantAdmins.copied') : t('tenantAdmins.copy')} withArrow>
          <ActionIcon variant="subtle" color={copied ? 'teal' : 'gray'} onClick={copy} disabled={!value}
            aria-label={t('tenantAdmins.copy')}>
            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
          </ActionIcon>
        </Tooltip>
      )}
    </CopyButton>
  );
}

interface StrongPasswordInputProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: React.ReactNode;
  description?: React.ReactNode;
  required?: boolean;
}

/**
 * Parol maydoni + "Kuchli parol yaratish" + nusxalash. Yaratilgan parol darhol
 * ko'rinadigan qilinadi — superadmin uni nusxalab adminga yetkazishi kerak.
 * Yangi maktab yaratishda ham, admin parolini qayta o'rnatishda ham ishlatiladi.
 */
export function StrongPasswordInput({ label, value, onChange, error, description, required }: StrongPasswordInputProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  return (
    <Stack gap={4}>
      <PasswordInput
        label={label}
        required={required}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        error={error}
        description={description}
        visible={visible}
        onVisibilityChange={setVisible}
        autoComplete="new-password"
        styles={{ input: { fontFamily: visible ? 'monospace' : undefined } }}
      />
      <Group gap="xs">
        <Button size="compact-xs" variant="light" leftSection={<IconSparkles size={14} />}
          onClick={() => { onChange(generateStrongPassword()); setVisible(true); }}>
          {t('tenantAdmins.generate')}
        </Button>
        {value && <CopyIcon value={value} />}
      </Group>
    </Stack>
  );
}

interface Admin {
  id: string; fullName: string; login: string; role: 'DIRECTOR' | 'ADMIN'; isActive: boolean; createdAt: string;
}

/**
 * Maktab adminlari (DIRECTOR/ADMIN) — platforma superadmini ularning parolini qayta
 * o'rnatadi (va xohlasa loginini almashtiradi). Backend:
 *   GET   /superadmin/tenants/:id/admins
 *   POST  /superadmin/tenants/:id/admins          { fullName, login, password, role, gender }
 *   PATCH /superadmin/tenants/:id/admins/:userId  { password?, login? }
 * Parol serverdan HECH QACHON qaytmaydi — faqat shu yerda, saqlashdan oldin/keyin
 * nusxalash uchun ko'rinadi va panel yopilishi bilan xotiradan o'chadi.
 */
export function TenantAdminsSection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Admin | null>(null);
  /** Saqlangandan keyingi xulosa — superadmin parolni nusxalab olishi uchun. */
  const [done, setDone] = useState<{ kind: 'created' | 'updated'; login: string; password: string } | null>(null);
  /** "Admin qo'shish" formasi ochiqmi (parol/login formasi bilan bir vaqtda ochilmaydi). */
  const [adding, setAdding] = useState(false);

  const { data: admins = [], isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['sa-tenant-admins', tenantId],
    queryFn: async () => (await api.get<Admin[]>(`/superadmin/tenants/${tenantId}/admins`)).data,
  });

  const form = useForm({
    initialValues: { login: '', password: '' },
    validate: {
      login: (v) => (v.trim().length < LOGIN_MIN ? t('tenantsPage.loginMin') : null),
      password: (v, values) => {
        if (v && v.length < TENANT_ADMIN_PASSWORD_MIN) return t('tenantsPage.passwordMin', { n: TENANT_ADMIN_PASSWORD_MIN });
        if (!v && values.login.trim() === selected?.login) return t('tenantAdmins.nothingToSave');
        return null;
      },
    },
  });

  const open = (a: Admin) => {
    setDone(null);
    setAdding(false);
    setSelected(a);
    form.setValues({ login: a.login, password: '' });
    form.resetDirty({ login: a.login, password: '' });
    form.clearErrors();
  };
  const close = () => { setSelected(null); setDone(null); form.reset(); };

  const saveMut = useMutation({
    mutationFn: async (v: { login: string; password: string }) => {
      const body: { login?: string; password?: string } = {};
      if (v.login.trim() !== selected!.login) body.login = v.login.trim();
      if (v.password) body.password = v.password;
      return (await api.patch<Admin>(`/superadmin/tenants/${tenantId}/admins/${selected!.id}`, body)).data;
    },
    onSuccess: (updated, v) => {
      qc.invalidateQueries({ queryKey: ['sa-tenant-admins', tenantId] });
      notifications.show({ color: 'green', message: t('tenantAdmins.saved', { name: updated.fullName }) });
      setDone({ kind: 'updated', login: updated.login, password: v.password });
      setSelected(null);
      form.reset();
    },
    onError: (e: unknown) => notifications.show({ color: 'red', message: errMsg(e, t('tenantsPage.error')) }),
  });

  // ── Admin qo'shish (maktabda admin qolmagan yoki yana biri kerak bo'lganda)
  const addForm = useForm({
    initialValues: { fullName: '', login: '', gender: '', role: 'DIRECTOR', password: '' },
    validate: {
      fullName: (v) => { const k = fullNameError(v); return k ? t(`settings.${k}`, { n: k === 'nameMax' ? FULL_NAME_MAX : FULL_NAME_MIN }) : null; },
      login: (v) => (v.trim().length < LOGIN_MIN ? t('tenantsPage.loginMin') : null),
      gender: (v) => (!v ? t('tenantAdmins.genderRequired') : null),
      password: (v) => (v.length < TENANT_ADMIN_PASSWORD_MIN ? t('tenantsPage.passwordMin', { n: TENANT_ADMIN_PASSWORD_MIN }) : null),
    },
  });
  const openAdd = () => {
    setDone(null);
    setSelected(null);
    form.reset();
    // Direktor yo'q bo'lsa — birinchi bo'lib direktor qo'shiladi; bor bo'lsa odatda admin
    const init = { fullName: '', login: '', gender: '', role: admins.some((a) => a.role === 'DIRECTOR') ? 'ADMIN' : 'DIRECTOR', password: '' };
    addForm.setValues(init);
    addForm.resetDirty(init);
    addForm.clearErrors();
    setAdding(true);
  };
  const closeAdd = () => { setAdding(false); addForm.reset(); };

  const addMut = useMutation({
    mutationFn: async (v: typeof addForm.values) =>
      (await api.post<Admin>(`/superadmin/tenants/${tenantId}/admins`, {
        fullName: cleanFullName(v.fullName), login: v.login.trim(), gender: v.gender, role: v.role, password: v.password,
      })).data,
    onSuccess: (created, v) => {
      qc.invalidateQueries({ queryKey: ['sa-tenant-admins', tenantId] });
      qc.invalidateQueries({ queryKey: ['sa-tenants'] }); // ro'yxatdagi foydalanuvchilar soni
      notifications.show({ color: 'green', message: t('tenantAdmins.added', { name: created.fullName }) });
      setDone({ kind: 'created', login: created.login, password: v.password });
      closeAdd();
    },
    onError: (e: unknown) => notifications.show({ color: 'red', message: errMsg(e, t('tenantsPage.error')) }),
  });

  const addButton = (
    <Button size="compact-sm" variant={adding ? 'filled' : 'light'} leftSection={<IconUserPlus size={14} />}
      onClick={() => (adding ? closeAdd() : openAdd())}>
      {t('tenantAdmins.add')}
    </Button>
  );

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
        <Text size="sm" c="dimmed">{t('tenantAdmins.hint')}</Text>
        {!isLoading && !isError && admins.length > 0 && addButton}
      </Group>

      {isLoading ? (
        <BlockLoader variant="table" />
      ) : isError ? (
        <Group gap="xs">
          <Text size="sm" c="red">{t('tenantAdmins.loadError')}</Text>
          <Button size="compact-xs" variant="light" color="red" leftSection={<IconRefresh size={14} />}
            loading={isFetching} onClick={() => void refetch()}>
            {t('tenantAdmins.retry')}
          </Button>
        </Group>
      ) : !admins.length ? (
        <Alert color="orange" variant="light" icon={<IconShieldLock size={18} />}>
          <Stack gap="xs" align="flex-start">
            <Text size="sm">{t('tenantAdmins.empty')}</Text>
            {addButton}
          </Stack>
        </Alert>
      ) : (
        <Stack gap={6}>
          {admins.map((a) => (
            <Paper key={a.id} withBorder radius="md" p="xs"
              bg={selected?.id === a.id ? 'var(--mantine-color-brand-light)' : undefined}>
              <Group justify="space-between" wrap="nowrap" gap="xs">
                <Stack gap={0} style={{ minWidth: 0 }}>
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm" fw={600} truncate>{a.fullName}</Text>
                    <Badge size="xs" variant="light" color={a.role === 'DIRECTOR' ? 'grape' : 'blue'}>{t(`roles.${a.role}`)}</Badge>
                    {!a.isActive && <Badge size="xs" variant="light" color="red">{t('block.blocked')}</Badge>}
                  </Group>
                  <Text size="xs" c="dimmed" ff="monospace" style={{ userSelect: 'all' }}>{a.login}</Text>
                </Stack>
                <Button size="compact-sm" variant={selected?.id === a.id ? 'filled' : 'light'}
                  leftSection={<IconKey size={14} />} onClick={() => (selected?.id === a.id ? close() : open(a))}>
                  {t('tenantAdmins.reset')}
                </Button>
              </Group>
            </Paper>
          ))}
        </Stack>
      )}

      {done && (
        <Alert color="green" variant="light" icon={<IconCheck size={18} />} withCloseButton onClose={() => setDone(null)}
          title={done.kind === 'created' ? t('tenantAdmins.createdTitle') : t('tenantAdmins.doneTitle')}>
          <Stack gap={4}>
            <Group gap={6} wrap="nowrap">
              <Text size="sm">{t('tenantsPage.adminLogin')}:</Text>
              <Text size="sm" ff="monospace" fw={600} style={{ userSelect: 'all' }}>{done.login}</Text>
              <CopyIcon value={done.login} />
            </Group>
            {done.password && (
              <Group gap={6} wrap="nowrap">
                <Text size="sm">{t('tenantAdmins.newPassword')}:</Text>
                <Text size="sm" ff="monospace" fw={600} style={{ userSelect: 'all' }}>{done.password}</Text>
                <CopyIcon value={done.password} />
              </Group>
            )}
            <Text size="xs" c="dimmed">{t('tenantAdmins.doneHint')}</Text>
          </Stack>
        </Alert>
      )}

      {adding && (
        <Paper withBorder radius="md" p="sm">
          <form onSubmit={addForm.onSubmit((v) => addMut.mutate(v))} noValidate>
            <Stack gap="sm">
              <Text size="sm" fw={600}>{t('tenantAdmins.addTitle')}</Text>
              <TextInput label={t('settings.fullName')} required maxLength={FULL_NAME_MAX} {...addForm.getInputProps('fullName')} />
              <TextInput label={t('tenantsPage.adminLogin')} required autoComplete="off"
                styles={{ input: { fontFamily: 'monospace' } }} {...addForm.getInputProps('login')} />
              <Group grow align="flex-start">
                <Select label={t('tenantAdmins.role')} required allowDeselect={false}
                  data={[
                    { value: 'DIRECTOR', label: t('roles.DIRECTOR') },
                    { value: 'ADMIN', label: t('roles.ADMIN') },
                  ]}
                  comboboxProps={{ withinPortal: true }}
                  {...addForm.getInputProps('role')} />
                <Stack gap={4}>
                  <Text size="sm" fw={500}>{t('tenantAdmins.gender')} <Text span c="red">*</Text></Text>
                  <SegmentedControl fullWidth
                    data={[
                      { value: 'MALE', label: t('tenantAdmins.male') },
                      { value: 'FEMALE', label: t('tenantAdmins.female') },
                    ]}
                    value={addForm.values.gender}
                    onChange={(v) => addForm.setFieldValue('gender', v)} />
                  {addForm.errors.gender && <Text size="xs" c="red">{addForm.errors.gender}</Text>}
                </Stack>
              </Group>
              <StrongPasswordInput
                label={t('tenantsPage.adminPassword')}
                required
                description={t('tenantAdmins.newAdminPasswordDesc', { n: TENANT_ADMIN_PASSWORD_MIN })}
                value={addForm.values.password}
                onChange={(v) => addForm.setFieldValue('password', v)}
                error={addForm.errors.password}
              />
              <Group justify="flex-end" gap="xs">
                <Button variant="default" size="xs" onClick={closeAdd}>{t('tenantsPage.cancel')}</Button>
                <Button type="submit" size="xs" loading={addMut.isPending} leftSection={<IconUserPlus size={14} />}>
                  {t('tenantAdmins.addSubmit')}
                </Button>
              </Group>
            </Stack>
          </form>
        </Paper>
      )}

      {selected && (
        <Paper withBorder radius="md" p="sm">
          <form onSubmit={form.onSubmit((v) => saveMut.mutate(v))} noValidate>
            <Stack gap="sm">
              <Text size="sm" fw={600}>{t('tenantAdmins.formTitle', { name: selected.fullName })}</Text>
              <TextInput label={t('tenantsPage.adminLogin')} autoComplete="off"
                description={t('tenantAdmins.loginDesc')} {...form.getInputProps('login')} />
              <StrongPasswordInput
                label={t('tenantAdmins.newPassword')}
                description={t('tenantAdmins.passwordDesc', { n: TENANT_ADMIN_PASSWORD_MIN })}
                value={form.values.password}
                onChange={(v) => form.setFieldValue('password', v)}
                error={form.errors.password}
              />
              <Text size="xs" c="dimmed">{t('tenantAdmins.sessionsNote')}</Text>
              <Group justify="flex-end" gap="xs">
                <Button variant="default" size="xs" onClick={close}>{t('tenantsPage.cancel')}</Button>
                <Button type="submit" size="xs" loading={saveMut.isPending}>{t('tenantsPage.save')}</Button>
              </Group>
            </Stack>
          </form>
        </Paper>
      )}
    </Stack>
  );
}
